import cn from "classnames";
import FileBody, { MultipleFileBody } from "components/shared/fileBody";
import Loader from "components/shared/loader";
import Popover from "components/shared/popover";
import WithMentions from "components/shared/withMentions";
import { dateTimeToView, dateToView } from "helpers/date";
import { userMessageFlagsEnum } from "helpers/enums";
import React, { useMemo, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { setAsUnread } from "sources/books/chats";
import { delItem, restore } from "sources/messages";

const Message = (props) => {
    const { createdBy, chatId, id, text, userId, createdById, index, messageId } = props;

    const [isDeleted, setDeleted] = useState(props.isDeleted);
    const [userMessagesInfo, setUserMessagesInfo] = useState(props.userMessagesInfo || []);
    const [isLoading, setLoading] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [actionsOpened, setActionsOpened] = useState(false);

    const mentioned = useMemo(() => {
        const myMessageInfo = userMessagesInfo.find((item) => item.recipient.id === userId);
        if (!myMessageInfo) {
            return false;
        }
        return myMessageInfo.flags.includes(userMessageFlagsEnum.Mentioned);
    }, [userMessagesInfo]);

    const cname = cn({
        "chat__body-item chat-msg": true,
        "chat-msg--my": createdById === userId,
        "chat-msg--other": createdById !== userId,
        "chat-msg--mentioned": mentioned,
    });

    const renderActions = () => {
        if (props.readOnly) {
            return isDeleted ? <small className="text-muted">Сообщение удалено.</small> : null;
        }
        if (isDeleted) {
            const restoreAction = () => {
                props.modal({
                    name: "confirm",
                    title: "Восстановление сообщения",
                    text: "Вы уверены, что хотите восстановить сообщение?",
                    buttons: { confirm: "Восстановить", cancel: "Отменить" },
                    action: () => {
                        setLoading(true);
                        return restore({ id: messageId ?? id, chatId }).then(() => {
                            setDeleted(false);
                            setLoading(false);
                        });
                    },
                });
            };
            return (
                <small className="text-muted">
                    Сообщение удалено.
                    {createdById === userId && (
                        <span className="chat-msg__restore-btn" onClick={restoreAction}>
                            Восстановить
                        </span>
                    )}
                </small>
            );
        } else {
            const items = [
                {
                    text: "Информация о сообщении",
                    action: (e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        setShowInfo(true);
                    },
                    available: userMessagesInfo.length > 0,
                },
                {
                    text: "Отметить как непрочитанное",
                    action: setMessageAsUnread,
                    available: userMessagesInfo.find((item) => item.recipient.id === userId)?.isRead,
                },
                { text: "Копировать", action: () => navigator.clipboard.writeText(text), available: !!text },
                { text: "Удалить", action: deleteAction, available: createdById === userId },
            ].filter((item) => item.available);
            return (
                <div className={cn("chat-msg__actions-wrap", { "chat-msg__actions-wrap--opened": actionsOpened })}>
                    <Popover
                        withoutChevron
                        onOpen={() => setActionsOpened(true)}
                        onClose={() => {
                            setShowInfo(false);
                            setActionsOpened(false);
                        }}
                        cname="chat-msg__actions"
                        text={<i className="icon-uEA0D-menu" />}
                        positions={index > 2 ? ["top"] : ["bottom"]}
                        align={createdById === userId ? "end" : "start"}
                    >
                        {showInfo ? (
                            <Scrollbars autoHeight autoHeightMin={100} autoHeightMax={300}>
                                <div className="chat-msg__info">
                                    {userMessagesInfo.map((item) => (
                                        <div key={item.id} className="pb-2">
                                            <b className="text-dark">{item.recipient.fullName}</b>
                                            <div>{item.recipient.bidRoles.join("/")}</div>
                                            <div>Доставлено: {dateTimeToView(item.createdAt)}</div>
                                            <div>Прочитано: {dateTimeToView(item.readAt)}</div>
                                        </div>
                                    ))}
                                </div>
                            </Scrollbars>
                        ) : (
                            items.map((item, i) => (
                                <div key={i} className="dropdown__item" onClick={item.action}>
                                    {item.text}
                                </div>
                            ))
                        )}
                    </Popover>
                </div>
            );
        }
    };

    const setMessageAsUnread = () => {
        setLoading(true);
        setAsUnread(chatId, messageId ?? id).then(() => {
            setUserMessagesInfo(
                userMessagesInfo.map((item) =>
                    item.recipient.id === userId ? { ...item, isRead: false, readAt: null } : item,
                ),
            );
            setLoading(false);
        });
    };

    const deleteAction = () => {
        props.modal({
            name: "confirm",
            title: "Удаление сообщения",
            text: "Вы уверены, что хотите удалить сообщение?",
            buttons: { confirm: "Удалить", cancel: "Отменить" },
            action: () => {
                setLoading(true);
                return delItem({ id: messageId ?? id, chatId }).then(() => {
                    setDeleted(true);
                    setLoading(false);
                });
            },
        });
    };

    const renderBody = () => {
        if (isDeleted) {
            return null;
        }
        const files = props.files?.length ? props.files : props.file ? [props.file] : [];
        return (
            <>
                {!!text && (
                    <div className="chat-msg__body chat-msg__body--text">
                        <WithMentions text={text} />
                    </div>
                )}
                {files.length > 1 && <MultipleFileBody {...props} files={files} />}
                {files.length === 1 && <FileBody {...props} file={files[0]} />}
            </>
        );
    };

    const { readByAllUsers, readByOneUser } = useMemo(
        () =>
            userMessagesInfo.reduce(
                (acc, item) =>
                    item.isIntegrationAccount
                        ? acc
                        : {
                              readByAllUsers: acc.readByAllUsers && item.isRead,
                              readByOneUser: acc.readByOneUser || item.isRead,
                          },
                { readByAllUsers: true, readByOneUser: false },
            ),
        [userMessagesInfo],
    );

    return (
        <div key={props.id} className={cname}>
            {isLoading && (
                <div className="loader-snippet--disable-loader">
                    <Loader />
                </div>
            )}
            {createdBy && createdById !== userId && (
                <div className="chat-msg__head">
                    <div className="d-flex justify-content-between">
                        <span className="pr-1 ellipsis">{createdBy.fullName}</span>
                    </div>
                </div>
            )}
            {renderActions()}
            {renderBody()}
            <span className="chat-msg__date">
                <span
                    className={cn({
                        "chat-msg__checkmark": !readByOneUser,
                        "chat-msg__double-checkmark": readByOneUser,
                        "chat-msg__double-checkmark--readed": readByAllUsers && readByOneUser,
                    })}
                />
                {dateToView(props.createdAt, "HH:mm")}
            </span>
        </div>
    );
};

export default Message;
